/**
 * APPLICATION CONSTANTS
 * =====================
 * 
 * Centralized constants for the Decisionr.xyz application.
 * Organized for easy configuration and Supabase integration.
 */

// ============================================================================
// APPLICATION METADATA
// ============================================================================

export const APP_NAME = 'Decisionr.xyz'
export const APP_DESCRIPTION = 'Track, rate, and learn from your important decisions over time'
export const APP_VERSION = '1.0.0'
export const APP_URL = 'https://decisionr.xyz'

// ============================================================================
// DECISION RATING SYSTEM
// ============================================================================

export const RATING_SCALE = {
  MIN: 1,
  MAX: 10,
  DEFAULT: 5
} as const

export const RATING_LABELS = {
  1: 'Terrible decision',
  2: 'Very poor',
  3: 'Poor',
  4: 'Below average',
  5: 'Average',
  6: 'Above average',
  7: 'Good',
  8: 'Very good',
  9: 'Excellent',
  10: 'Perfect decision'
} as const

// ============================================================================
// REVIEW INTERVALS
// ============================================================================

export const DEFAULT_REVIEW_INTERVALS = [7, 30, 90] as const

export const REVIEW_INTERVAL_OPTIONS = [
  { value: 1, label: '1 day' },
  { value: 3, label: '3 days' },
  { value: 7, label: '1 week' },
  { value: 14, label: '2 weeks' },
  { value: 30, label: '1 month' },
  { value: 60, label: '2 months' },
  { value: 90, label: '3 months' },
  { value: 180, label: '6 months' },
  { value: 365, label: '1 year' }
] as const

// ============================================================================
// PREDEFINED TAGS
// ============================================================================

export const SUGGESTED_TAGS = [
  'career',
  'finance',
  'health',
  'relationships',
  'education',
  'technology',
  'business',
  'personal',
  'family',
  'travel',
  'investment',
  'lifestyle',
  'project',
  'strategy',
  'urgent',
  'important',
  'risky',
  'safe'
] as const

export const TAG_COLORS = [
  '#ef4444', // red
  '#f97316', // orange
  '#eab308', // yellow
  '#22c55e', // green
  '#06b6d4', // cyan
  '#3b82f6', // blue
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#64748b', // slate
  '#10b981'  // emerald
] as const

// ============================================================================
// PREDEFINED EMOTIONS
// ============================================================================

export const SUGGESTED_EMOTIONS = [
  'confident',
  'uncertain',
  'excited',
  'anxious',
  'optimistic',
  'pessimistic',
  'calm',
  'stressed',
  'motivated',
  'hesitant',
  'determined',
  'conflicted',
  'hopeful',
  'worried',
  'enthusiastic',
  'doubtful',
  'relieved',
  'pressured'
] as const

// ============================================================================
// UI CONSTANTS
// ============================================================================

export const THEME_OPTIONS = ['light', 'dark', 'system'] as const

export const SIDEBAR_WIDTH = {
  EXPANDED: 280,
  COLLAPSED: 64
} as const

export const BREAKPOINTS = {
  SM: 640,
  MD: 768,
  LG: 1024,
  XL: 1280,
  '2XL': 1536
} as const

// ============================================================================
// PAGINATION
// ============================================================================

export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100,
  PAGE_SIZE_OPTIONS: [10, 20, 50, 100]
} as const

// ============================================================================
// DATE FORMATS
// ============================================================================

export const DATE_FORMATS = {
  ISO: 'YYYY-MM-DD',
  DISPLAY: 'MMM DD, YYYY',
  INPUT: 'YYYY-MM-DD',
  TIMESTAMP: 'YYYY-MM-DD HH:mm:ss'
} as const

// ============================================================================
// LOCAL STORAGE KEYS
// ============================================================================

export const STORAGE_KEYS = {
  THEME: 'decisionr-theme',
  SIDEBAR_COLLAPSED: 'decisionr-sidebar-collapsed',
  USER_PREFERENCES: 'decisionr-user-preferences',
  DRAFT_DECISION: 'decisionr-draft-decision'
} as const

// ============================================================================
// API ENDPOINTS (for future Supabase integration)
// ============================================================================

export const API_ENDPOINTS = {
  DECISIONS: '/api/decisions',
  RATINGS: '/api/ratings',
  REVIEWS: '/api/reviews',
  TAGS: '/api/tags',
  EMOTIONS: '/api/emotions',
  ANALYTICS: '/api/analytics',
  ACTIVITY: '/api/activity',
  PROFILE: '/api/profile'
} as const

// ============================================================================
// ERROR MESSAGES
// ============================================================================

export const ERROR_MESSAGES = {
  GENERIC: 'Something went wrong. Please try again.',
  NETWORK: 'Network error. Please check your connection.',
  VALIDATION: 'Please check your input and try again.',
  UNAUTHORIZED: 'You need to sign in to access this feature.',
  NOT_FOUND: 'The requested resource was not found.',
  SERVER_ERROR: 'Server error. Please try again later.'
} as const

// ============================================================================
// SUCCESS MESSAGES
// ============================================================================

export const SUCCESS_MESSAGES = {
  DECISION_CREATED: 'Decision created successfully!',
  DECISION_UPDATED: 'Decision updated successfully!',
  DECISION_DELETED: 'Decision deleted successfully!',
  RATING_SUBMITTED: 'Rating submitted successfully!',
  PROFILE_UPDATED: 'Profile updated successfully!',
  SETTINGS_SAVED: 'Settings saved successfully!'
} as const

// ============================================================================
// VALIDATION RULES
// ============================================================================

export const VALIDATION = {
  DECISION_TITLE: {
    MIN_LENGTH: 3,
    MAX_LENGTH: 500
  },
  DECISION_DESCRIPTION: {
    MAX_LENGTH: 2000
  },
  TAG_NAME: {
    MIN_LENGTH: 1,
    MAX_LENGTH: 50
  },
  RATING_NOTES: {
    MAX_LENGTH: 1000
  }
} as const

// ============================================================================
// ANIMATION DURATIONS
// ============================================================================

export const ANIMATION = {
  FAST: 150,
  NORMAL: 300,
  SLOW: 500
} as const

// ============================================================================
// FEATURE FLAGS (for gradual rollout)
// ============================================================================

export const FEATURES = {
  ANALYTICS_DASHBOARD: true,
  EXPORT_DATA: true,
  TEAM_DECISIONS: false, // Future feature
  AI_INSIGHTS: false,    // Future feature
  DECISION_TEMPLATES: false // Future feature
} as const